/* App root with the public Klantenservice landing redirect, customer-service pages, admin routes, and Buitn Planten metadata.
   Updated: Added the floating WhatsApp button next to the back-to-top control.
   Updated: Added the admin Transactiemails page (Brevo mail route, test mails, mail queue).
   Updated: Removed the duplicate gtag.js install (GA4 now loads only once from index.html) and
   added AnalyticsRouteGuard, which disables GA4 while navigating inside /kh-admin. */

import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { CartProvider } from './contexts/CartContext';
import { FavoritesProvider } from './contexts/FavoritesContext';
import { Toaster } from './components/ui/sonner';
import BackToTop from './components/BackToTop';
import WhatsAppButton from './components/WhatsAppButton';
import { COMPANY_INFO } from './lib/company-info';
import { supabase } from './lib/supabase';
import { setGa4DisabledForPath } from './lib/analytics';

import HomePage from './pages/HomePage';
import CategoryPage from './pages/CategoryPage';
import AanplantEnVerzorgingPage from './pages/AanplantEnVerzorgingPage';
import AanplantservicePage from './pages/AanplantservicePage';
import CartPage from './pages/CartPage';
import CartOverviewPage from './pages/CartOverviewPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import OfferteAanvragenPage from './pages/OfferteAanvragenPage';
import BlogsPage from './pages/BlogsPage';
import BlogDetailPage from './pages/BlogDetailPage';
import BlogPreviewPage from './pages/BlogPreviewPage';
import ProductPage from './pages/ProductPage';
import CategorySlugRouter from './components/CategorySlugRouter';
import KlantenservicePage from './pages/KlantenservicePage';
import PrivacyPage from './pages/PrivacyPage';
import AlgemeneVoorwaardenPage from './pages/AlgemeneVoorwaardenPage';
import NotFoundPage from './pages/NotFoundPage';
import ReviewsPage from './pages/ReviewsPage';

import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminLogin from './pages/admin/AdminLogin';
import AdminAdmins from './pages/admin/AdminAdmins';
import AdminAssortiment from './pages/admin/AdminAssortiment';
import AdminBlogs from './pages/admin/AdminBlogs';
import AdminBlogEditor from './pages/admin/AdminBlogEditor';
import AdminBlogAutomatisering from './pages/admin/AdminBlogAutomatisering';
import AdminDeliverySettings from './pages/admin/AdminDeliverySettings';
import AdminFAQ from './pages/admin/AdminFAQ';
import AdminKlantenservice from './pages/admin/AdminKlantenservice';
import AdminKortingscodes from './pages/admin/AdminKortingscodes';
import AdminMailing from './pages/admin/AdminMailing';
import AdminNewsletter from './pages/admin/AdminNewsletter';
import AdminOrders from './pages/admin/AdminOrders';
import AdminQuoteRequests from './pages/admin/AdminQuoteRequests';
import AdminReviews from './pages/admin/AdminReviews';
import AdminSubcategories from './pages/admin/AdminSubcategories';
import AdminPlantPricing from './pages/admin/AdminPlantPricing';
import AdminDefaultMargins from './pages/admin/AdminDefaultMargins';
import AdminTransactionalMail from './pages/admin/AdminTransactionalMail';
import AdminBusinessCustomers from './pages/admin/AdminBusinessCustomers';
import AdminBusinessPricelist from './pages/admin/AdminBusinessPricelist';
import AdminBusinessOrders from './pages/admin/AdminBusinessOrders';
import ZakelijkLoginPage from './pages/ZakelijkLoginPage';
import ZakelijkWachtwoordPage from './pages/ZakelijkWachtwoordPage';
import ZakelijkDashboardPage from './pages/ZakelijkDashboardPage';

function DataFastCrawlerBeacon() {
  const location = useLocation();

  useEffect(() => {
    const beacon = async () => {
      try {
        const { data } = await supabase.functions.invoke('track-ai-crawler', {
          body: {
            path: location.pathname + location.search,
            userAgent: navigator.userAgent,
            referer: document.referrer,
          },
        });
        return data;
      } catch {
        // Silent fail — tracking is best-effort
      }
    };
    beacon();
  }, [location.pathname, location.search]);

  return null;
}

function AnalyticsRouteGuard() {
  const { pathname } = useLocation();

  useEffect(() => {
    setGa4DisabledForPath(pathname);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <>
      <AnalyticsRouteGuard />
      <DataFastCrawlerBeacon />
      <Helmet>
        <title>Buitn Planten | Alles voor buiten, groen samengebracht</title>
        <meta name="description" content="Haagplanten, bomen en tuinplanten voor een buitenruimte die klopt. Buitn Planten, onderdeel van Buitn.nl." />
        <link rel="canonical" href="https://buitnplanten.nl/" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": "https://buitnplanten.nl/#localbusiness",
            "name": COMPANY_INFO.name,
            "description": "Buitn Planten levert topkwaliteit haagplanten, bomen en tuinplanten direct van de kweker uit Dedemsvaart. Met aanplantmaterialen en aanplantservice.",
            "url": `https://${COMPANY_INFO.website}`,
            "telephone": COMPANY_INFO.phone,
            "email": COMPANY_INFO.email,
            "address": {
              "@type": "PostalAddress",
              "streetAddress": COMPANY_INFO.address,
              "addressLocality": COMPANY_INFO.city,
              "addressRegion": "Overijssel",
              "postalCode": COMPANY_INFO.postalCode,
              "addressCountry": "NL"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "52.596389",
              "longitude": "6.461111"
            },
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                "opens": "08:00",
                "closes": "17:00"
              },
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": "Saturday",
                "opens": "09:00",
                "closes": "16:00"
              }
            ],
            "priceRange": "€€",
            "areaServed": {
              "@type": "Country",
              "name": "Nederland"
            },
            "image": "https://buitnplanten.nl/assets/sections/hero.webp",
            "logo": "https://buitnplanten.nl/assets/laurier-primary.webp",
            "paymentAccepted": "Cash, Credit Card, Bank Transfer",
            "currenciesAccepted": "EUR",
            "vatID": COMPANY_INFO.btw,
            "taxID": COMPANY_INFO.kvk,
            "sameAs": [
              "https://www.marktplaats.nl/u/jv-garden-plants",
              "https://www.instagram.com/buitnplanten.nl/",
              "https://share.google/rRuGOX9cwYp6vbKci"
            ]
          })}
        </script>

        {/* Organization Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "@id": "https://buitnplanten.nl/#organization",
            "name": COMPANY_INFO.name,
            "url": `https://${COMPANY_INFO.website}`,
            "logo": "https://buitnplanten.nl/assets/laurier-primary.webp",
            "email": COMPANY_INFO.email,
            "telephone": COMPANY_INFO.phone,
            "foundingDate": "2024",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": COMPANY_INFO.address,
              "addressLocality": COMPANY_INFO.city,
              "addressRegion": "Overijssel",
              "postalCode": COMPANY_INFO.postalCode,
              "addressCountry": "NL"
            },
            "vatID": COMPANY_INFO.btw,
            "taxID": COMPANY_INFO.kvk,
            "sameAs": [
              "https://www.marktplaats.nl/u/jv-garden-plants",
              "https://www.instagram.com/buitnplanten.nl/",
              "https://share.google/rRuGOX9cwYp6vbKci"
            ]
          })}
        </script>

      </Helmet>

      <CartProvider>
        <FavoritesProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/haagplanten" element={<CategoryPage category="haagplanten" />} />
          <Route path="/aanplant-en-verzorging" element={<AanplantEnVerzorgingPage />} />
          <Route path="/assortiment" element={<Navigate to="/haagplanten" replace />} />
          <Route path="/bomen" element={<CategoryPage category="bomen" />} />
          <Route path="/tuinplanten" element={<CategoryPage category="tuinplanten" />} />
          <Route path="/contact" element={<Navigate to="/klantenservice/contact" replace />} />
          <Route path="/winkelwagen" element={<CartOverviewPage />} />
          <Route path="/winkelwagen/afrekenen" element={<CartPage />} />
          <Route path="/afrekenen/winkelwagen" element={<Navigate to="/winkelwagen/afrekenen" replace />} />
          <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
          <Route path="/projectprijs-aanvragen" element={<OfferteAanvragenPage />} />
          <Route path="/offerte-aanvragen" element={<Navigate to="/projectprijs-aanvragen" replace />} />
          <Route path="/blogs" element={<BlogsPage />} />
          <Route path="/blogs/:slug" element={<BlogDetailPage />} />
          <Route path="/preview-blog/:slug" element={<BlogPreviewPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/klantenservice" element={<Navigate to="/klantenservice/aanplantservice" replace />} />
          <Route path="/klantenservice/aanplantservice" element={<AanplantservicePage />} />
          <Route path="/klantenservice/:slug" element={<KlantenservicePage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/algemene-voorwaarden" element={<AlgemeneVoorwaardenPage />} />
          <Route path="/:category/:product" element={<CategorySlugRouter />} />

          {/* Zakelijk routes */}
          <Route path="/zakelijk/inloggen" element={<ZakelijkLoginPage />} />
          <Route path="/zakelijk/wachtwoord-wijzigen" element={<ZakelijkWachtwoordPage />} />
          <Route path="/zakelijk" element={<ZakelijkDashboardPage />} />

          {/* Admin routes */}
          <Route path="/kh-admin/login" element={<AdminLogin />} />
          <Route path="/kh-admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="admins" element={<AdminAdmins />} />
            <Route path="assortiment" element={<AdminAssortiment />} />
            <Route path="blogs" element={<AdminBlogs />} />
            <Route path="blogs/editor" element={<AdminBlogEditor />} />
            <Route path="blogs/automatisering" element={<AdminBlogAutomatisering />} />
            <Route path="delivery-settings" element={<AdminDeliverySettings />} />
            <Route path="delivery-calculator" element={<Navigate to="/kh-admin/delivery-settings" replace />} />
            <Route path="plant-pricing" element={<AdminPlantPricing />} />
            <Route path="default-margins" element={<AdminDefaultMargins />} />
            <Route path="faq" element={<AdminFAQ />} />
            <Route path="klantenservice" element={<AdminKlantenservice />} />
            <Route path="kortingscodes" element={<AdminKortingscodes />} />
            <Route path="mailing" element={<AdminMailing />} />
            <Route path="newsletter" element={<AdminNewsletter />} />
            <Route path="transactiemails" element={<AdminTransactionalMail />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="quote-requests" element={<AdminQuoteRequests />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="subcategories" element={<AdminSubcategories />} />
            <Route path="zakelijk/klanten" element={<AdminBusinessCustomers />} />
            <Route path="zakelijk/prijslijst" element={<AdminBusinessPricelist />} />
            <Route path="zakelijk/bestellingen" element={<AdminBusinessOrders />} />
            <Route path="invoices" element={<Navigate to="/kh-admin/orders?new=true" replace />} />
            <Route path="quotes" element={<Navigate to="/kh-admin/quote-requests?tab=offertes" replace />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>

        <Toaster position="bottom-right" />
        <BackToTop />
        <WhatsAppButton />
        </FavoritesProvider>
      </CartProvider>
    </>
  );
}

export default App;
